import { cur } from '@/lib/currency';
import { useEffect, useState, useMemo } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import MatchCandidatesPicker from '@/components/MatchCandidatesPicker';
import {
  CheckCircle, AlertTriangle, XCircle, Flag, ArrowDownUp, Search,
  Download, CheckCheck, TrendingUp, TrendingDown, Percent, Wand2, Link, Unlink,
  Bell, Clock, Settings, Play, Shield, AlertOctagon
} from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { useLanguage } from '@/contexts/LanguageContext';

const STATUS_CONFIG = {
  matched: { label: 'Matched', icon: CheckCircle, color: 'bg-success/20 text-success border-success/30 dark:bg-emerald-900/30 dark:text-emerald-400 dark:border-emerald-800' },
  mismatch: { label: 'Mismatch', icon: AlertTriangle, color: 'bg-warning/20 text-warning border-warning/30 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-800' },
  bank_only: { label: 'Bank Only', icon: XCircle, color: 'bg-error/20 text-error border-error/30 dark:bg-red-900/30 dark:text-red-400 dark:border-red-800' },
  app_only: { label: 'App Only', icon: XCircle, color: 'bg-info/20 text-info border-info/30 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-800' },
};

const FLAG_OPTIONS = [
  { value: '', label: 'No Flag' },
  { value: 'verified', label: 'Verified OK' },
  { value: 'investigate', label: 'Needs Investigation' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'ignored', label: 'Ignored' },
];

function StatusPieChart({ summary }) {
  const total = (summary.matched_count || 0) + (summary.discrepancy_count || 0);
  if (total === 0) return null;
  const matchPct = Math.round(((summary.matched_count || 0) / total) * 100);
  const mismatchPct = 100 - matchPct;
  const circumference = 2 * Math.PI * 40;
  const matchLen = (matchPct / 100) * circumference;
  const mismatchLen = circumference - matchLen;

  return (
    <div className="flex items-center gap-4">
      <svg width="96" height="96" viewBox="0 0 100 100" className="transform -rotate-90">
        <circle cx="50" cy="50" r="40" fill="none" stroke="#e2e8f0" strokeWidth="12" className="dark:stroke-stone-700" />
        <circle cx="50" cy="50" r="40" fill="none" stroke="#22c55e" strokeWidth="12"
          strokeDasharray={`${matchLen} ${circumference}`} strokeLinecap="round" />
        {mismatchPct > 0 && (
          <circle cx="50" cy="50" r="40" fill="none" stroke="#f59e0b" strokeWidth="12"
            strokeDasharray={`${mismatchLen} ${circumference}`} strokeDashoffset={`-${matchLen}`} strokeLinecap="round" />
        )}
      </svg>
      <div className="space-y-1 text-xs">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-success" />
          <span className="dark:text-muted-foreground">Matched: {summary.matched_count || 0} ({matchPct}%)</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-warning" />
          <span className="dark:text-muted-foreground">Issues: {summary.discrepancy_count || 0} ({mismatchPct}%)</span>
        </div>
      </div>
    </div>
  );
}

export default function ReconciliationPage() {
  const { t } = useLanguage();
  const [statements, setStatements] = useState([]);
  const [selectedStmt, setSelectedStmt] = useState('');
  const [reconciliation, setReconciliation] = useState(null);
  const [flags, setFlags] = useState({});
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [flagDialog, setFlagDialog] = useState(null);
  const [flagForm, setFlagForm] = useState({ flag: '', notes: '' });
  const [autoMatches, setAutoMatches] = useState([]);
  const [matchStatusFilter, setMatchStatusFilter] = useState('all'); // all | auto | confirmed | auto_confirmed
  const [matchEntityFilter, setMatchEntityFilter] = useState('all'); // all | customer | supplier | partner | employee
  const [matchLoading, setMatchLoading] = useState(false);
  const [matchTab, setMatchTab] = useState('reconciliation');
  const [unmatchedData, setUnmatchedData] = useState(null);
  const [unmatchedLoading, setUnmatchedLoading] = useState(false);
  const [tolerance, setTolerance] = useState('5');
  const [dateRange, setDateRange] = useState('3');
  const [alertHistory, setAlertHistory] = useState([]);
  const [alertSettings, setAlertSettings] = useState(null);
  const [alertLoading, setAlertLoading] = useState(false);
  const [runningAlert, setRunningAlert] = useState(false);

  useEffect(() => {
    // iter410 — Auto-select the most recent statement on load. The page
    // used to render with an empty "Choose a statement..." dropdown,
    // making the page look broken/empty until the user clicked into it.
    // Now we pick the first (most-recent) statement and trigger the
    // reconciliation fetch immediately so the page lands ready.
    api.get('/bank-statements').then(r => {
      const list = Array.isArray(r.data) ? r.data : (r.data?.items || []);
      setStatements(list);
      if (list.length > 0 && !selectedStmt) {
        handleStmtChange(list[0].id);
      }
    }).catch(() => {});
    loadAlertData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadAlertData = async () => {
    setAlertLoading(true);
    try {
      const [histRes, settRes] = await Promise.all([
        api.get('/reconciliation-alerts'),
        api.get('/reconciliation-alerts/settings'),
      ]);
      setAlertHistory(histRes.data);
      setAlertSettings(settRes.data);
    } catch {}
    finally { setAlertLoading(false); }
  };

  const runAlertNow = async () => {
    setRunningAlert(true);
    try {
      const threshold = alertSettings?.threshold || 500;
      const { data } = await api.post('/reconciliation-alerts/run', { threshold });
      toast.success(`Alert generated: ${data.alert?.total_flagged || 0} flagged transactions`);
      loadAlertData();
    } catch { toast.error('Failed to run alert'); }
    finally { setRunningAlert(false); }
  };

  const saveAlertSettings = async (updates) => {
    const newSettings = { ...alertSettings, ...updates };
    try {
      const { data } = await api.put('/reconciliation-alerts/settings', newSettings);
      setAlertSettings(data);
      toast.success('Alert settings saved');
    } catch { toast.error('Failed to save settings'); }
  };

  const loadReconciliation = async (stmtId) => {
    if (!stmtId) return;
    setLoading(true);
    try {
      const [reconRes, flagsRes] = await Promise.all([
        api.get(`/bank-statements/${stmtId}/reconciliation`),
        api.get(`/bank-statements/${stmtId}/reconciliation/flags`)
      ]);
      setReconciliation(reconRes.data);
      setFlags(flagsRes.data);
    } catch { toast.error('Failed to load reconciliation'); }
    finally { setLoading(false); }
  };

  const handleStmtChange = (val) => { setSelectedStmt(val); loadReconciliation(val); loadAutoMatches(val); loadUnmatched(val); };

  const loadAutoMatches = async (stmtId) => {
    if (!stmtId) return;
    try {
      const { data } = await api.get(`/bank-statements/${stmtId}/matches`);
      setAutoMatches(data);
    } catch {}
  };

  const loadUnmatched = async (stmtId) => {
    if (!stmtId) return;
    setUnmatchedLoading(true);
    try {
      const { data } = await api.get(`/bank-statements/${stmtId}/unmatched`);
      setUnmatchedData(data);
    } catch {}
    finally { setUnmatchedLoading(false); }
  };

  const runAutoMatch = async () => {
    if (!selectedStmt) return;
    setMatchLoading(true);
    try {
      const { data } = await api.post(`/bank-statements/${selectedStmt}/auto-match?tolerance=${tolerance}&date_range=${dateRange}`);
      toast.success(`Auto-matched ${data.stats.auto_matched} transactions, ${data.stats.unmatched} unmatched`);
      setAutoMatches(prev => [...prev, ...data.matched]);
      loadUnmatched(selectedStmt);
    } catch { toast.error('Auto-match failed'); }
    finally { setMatchLoading(false); }
  };

  const manualLink = async (txnIndex, matchType, matchId) => {
    try {
      const { data } = await api.post(`/bank-statements/${selectedStmt}/manual-match`, {
        txn_index: txnIndex, match_type: matchType, match_id: matchId
      });
      setAutoMatches(prev => [...prev, data]);
      setUnmatchedData(prev => prev ? { ...prev, unmatched: prev.unmatched.filter(u => u.index !== txnIndex), total: prev.total - 1 } : prev);
      toast.success('Transaction manually linked');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to link');
    }
  };

  const confirmMatch = async (matchId) => {
    try {
      await api.post(`/bank-statements/${selectedStmt}/matches/${matchId}/confirm`);
      setAutoMatches(prev => prev.map(m => m.id === matchId ? { ...m, status: 'confirmed' } : m));
      toast.success('Match confirmed');
    } catch { toast.error('Failed to confirm'); }
  };

  const rejectMatch = async (matchId) => {
    try {
      await api.delete(`/bank-statements/${selectedStmt}/matches/${matchId}`);
      setAutoMatches(prev => prev.filter(m => m.id !== matchId));
      toast.success('Match rejected');
    } catch { toast.error('Failed to reject'); }
  };

  const saveFlag = async () => {
    if (!flagDialog || !selectedStmt) return;
    try {
      await api.post(`/bank-statements/${selectedStmt}/reconciliation/flag`, {
        row_key: flagDialog.row_key, flag: flagForm.flag, notes: flagForm.notes
      });
      setFlags(prev => ({ ...prev, [flagDialog.row_key]: { flag: flagForm.flag, notes: flagForm.notes } }));
      toast.success('Flag saved');
      setFlagDialog(null);
    } catch { toast.error('Failed to save flag'); }
  };

  const batchVerifyMatched = async () => {
    if (!selectedStmt || !reconciliation) return;
    const matchedRows = rows.filter(r => r.status === 'matched');
    let count = 0;
    for (const row of matchedRows) {
      const rowKey = `${row.deposit_date}|${row.branch}`;
      if (!flags[rowKey]?.flag) {
        try {
          await api.post(`/bank-statements/${selectedStmt}/reconciliation/flag`, {
            row_key: rowKey, flag: 'verified', notes: 'Auto-verified (batch)'
          });
          count++;
        } catch {}
      }
    }
    toast.success(`${count} rows verified`);
    loadReconciliation(selectedStmt);
  };

  const exportCSV = () => {
    if (!reconciliation) return;
    const headers = ['Deposit Date', 'Sale Date', 'Branch', 'Bank POS (SR)', 'App Sales (SR)', 'Difference', 'Diff %', 'Status', 'Flag'];
    const csvRows = [headers.join(',')];
    rows.forEach(row => {
      const rowKey = `${row.deposit_date}|${row.branch}`;
      const flag = flags[rowKey]?.flag || '';
      const diffPct = row.bank_amount > 0 ? ((row.difference / row.bank_amount) * 100).toFixed(1) : '0';
      csvRows.push([row.deposit_date, row.sale_date || '', row.branch, row.bank_amount, row.app_amount, row.difference, diffPct + '%', row.status, flag].join(','));
    });
    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'reconciliation_export.csv'; a.click();
    URL.revokeObjectURL(url);
    toast.success('CSV exported');
  };

  const rows = reconciliation?.rows || [];
  const summary = reconciliation?.summary || {};

  const filteredRows = useMemo(() => {
    return rows.filter(row => {
      if (statusFilter !== 'all' && row.status !== statusFilter) return false;
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        return row.branch?.toLowerCase().includes(term) || row.deposit_date?.includes(term) || row.sale_date?.includes(term);
      }
      return true;
    });
  }, [rows, statusFilter, searchTerm]);

  const verifiedCount = Object.values(flags).filter(f => f.flag === 'verified').length;
  const investigateCount = Object.values(flags).filter(f => f.flag === 'investigate').length;

  return (
    <DashboardLayout>
      <div className="space-y-5" data-testid="reconciliation-page">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-4">

              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-lg shadow-emerald-500/20">

                <CheckCircle size={24} className="text-white" />

              </div>

              <div>

                <h1 className="text-2xl sm:text-3xl font-bold font-outfit tracking-tight text-foreground" data-testid="reconciliation-title">Reconciliation</h1>

                <p className="text-sm text-muted-foreground mt-0.5">Account reconciliation</p>

              </div>

            </div>
            <p className="text-sm text-muted-foreground mt-0.5">Match bank deposits against Smarto sales</p>
          </div>
          {reconciliation && (
            <div className="flex gap-2 flex-wrap">
              <div className="flex items-center gap-1.5 bg-muted/50 dark:bg-muted rounded-lg px-2 py-1">
                <span className="text-[10px] text-muted-foreground">Tolerance:</span>
                <Select value={tolerance} onValueChange={setTolerance}>
                  <SelectTrigger className="h-7 w-16 text-xs border-0 bg-transparent p-0" data-testid="tolerance-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">{cur()} 1</SelectItem>
                    <SelectItem value="5">{cur()} 5</SelectItem>
                    <SelectItem value="10">{cur()} 10</SelectItem>
                    <SelectItem value="50">{cur()} 50</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-1.5 bg-muted/50 dark:bg-muted rounded-lg px-2 py-1">
                <span className="text-[10px] text-muted-foreground">Date Range:</span>
                <Select value={dateRange} onValueChange={setDateRange}>
                  <SelectTrigger className="h-7 w-20 text-xs border-0 bg-transparent p-0" data-testid="daterange-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">1 day</SelectItem>
                    <SelectItem value="2">2 days</SelectItem>
                    <SelectItem value="3">3 days</SelectItem>
                    <SelectItem value="5">5 days</SelectItem>
                    <SelectItem value="7">7 days</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button variant="outline" size="sm" onClick={runAutoMatch} disabled={matchLoading} data-testid="auto-match-btn" className="border-orange-200 text-orange-600 hover:bg-orange-50">
                <Wand2 size={14} className="mr-1" />{matchLoading ? 'Matching...' : 'Auto-Match'}
              </Button>
              <Button variant="outline" size="sm" onClick={batchVerifyMatched} data-testid="batch-verify-btn">
                <CheckCheck size={14} className="mr-1" />Verify All Matched
              </Button>
              <Button variant="outline" size="sm" onClick={exportCSV} data-testid="export-csv-btn">
                <Download size={14} className="mr-1" />Export CSV
              </Button>
            </div>
          )}
        </div>

        {/* Statement Selector */}
        <Card className="dark:bg-foreground dark:border-stone-700">
          <CardContent className="pt-5">
            <div className="flex items-end gap-4 flex-wrap">
              <div className="flex-1 max-w-sm">
                <Label className="text-xs mb-1 block">Select Bank Statement</Label>
                <Select value={selectedStmt} onValueChange={handleStmtChange}>
                  <SelectTrigger data-testid="stmt-select" className="h-9"><SelectValue placeholder="Choose a statement..." /></SelectTrigger>
                  <SelectContent>
                    {statements.map(s => (
                      <SelectItem key={s.id} value={s.id}>{s.file_name || s.bank_name} ({s.period || 'N/A'})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {reconciliation && (
                <div className="flex gap-2 items-end">
                  <div>
                    <Label className="text-xs mb-1 block">Filter Status</Label>
                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                      <SelectTrigger className="h-9 w-36" data-testid="status-filter"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All</SelectItem>
                        <SelectItem value="matched">Matched</SelectItem>
                        <SelectItem value="mismatch">Mismatch</SelectItem>
                        <SelectItem value="bank_only">Bank Only</SelectItem>
                        <SelectItem value="app_only">App Only</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="relative">
                    <Search size={14} className="absolute left-2.5 top-2.5 text-muted-foreground" />
                    <Input placeholder="Search branch or date..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                      className="h-9 pl-8 w-52" data-testid="recon-search" />
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Summary with Pie Chart */}
        {reconciliation && (
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
            <div className="lg:col-span-3">
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-3" data-testid="recon-summary">
                {[
                  { label: 'Bank POS Total', value: `${cur()} ${(summary.total_bank_pos || 0).toLocaleString()}`, color: 'text-info', icon: TrendingUp },
                  { label: 'App Sales Total', value: `${cur()} ${(summary.total_app_sales || 0).toLocaleString()}`, color: 'text-success', icon: TrendingUp },
                  { label: 'Difference', value: `${cur()} ${(summary.total_difference || 0).toLocaleString()}`, color: summary.total_difference > 0 ? 'text-warning' : 'text-success', icon: TrendingDown },
                  { label: 'Verified', value: verifiedCount, color: 'text-success', icon: CheckCircle },
                  { label: 'Investigate', value: investigateCount, color: 'text-warning', icon: AlertTriangle },
                ].map((c, i) => (
                  <Card key={i} className="dark:bg-foreground dark:border-stone-700">
                    <CardContent className="pt-4 pb-3 px-4">
                      <div className="flex items-center gap-1.5 mb-1">
                        <c.icon size={13} className="text-muted-foreground" />
                        <p className="text-xs text-muted-foreground">{c.label}</p>
                      </div>
                      <p className={`text-lg font-bold font-outfit ${c.color}`}>{c.value}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
            <Card className="dark:bg-foreground dark:border-stone-700">
              <CardContent className="pt-4 flex items-center justify-center">
                <StatusPieChart summary={summary} />
              </CardContent>
            </Card>
          </div>
        )}

        {/* Tab Toggle */}
        {reconciliation && (
          <div className="flex gap-1 bg-muted dark:bg-muted p-1 rounded-xl w-fit" data-testid="recon-tabs">
            <button onClick={() => setMatchTab('reconciliation')}
              className={`px-4 py-1.5 text-sm rounded-lg transition-colors ${matchTab === 'reconciliation' ? 'bg-white dark:bg-stone-700 shadow font-medium' : 'text-muted-foreground hover:text-foreground'}`}>
              POS Reconciliation ({rows.length})
            </button>
            <button onClick={() => setMatchTab('auto-match')}
              className={`px-4 py-1.5 text-sm rounded-lg transition-colors flex items-center gap-1.5 ${matchTab === 'auto-match' ? 'bg-white dark:bg-stone-700 shadow font-medium' : 'text-muted-foreground hover:text-foreground'}`}>
              <Link size={13} />Matched ({autoMatches.length})
            </button>
            <button onClick={() => setMatchTab('unmatched')}
              className={`px-4 py-1.5 text-sm rounded-lg transition-colors flex items-center gap-1.5 ${matchTab === 'unmatched' ? 'bg-white dark:bg-stone-700 shadow font-medium' : 'text-muted-foreground hover:text-foreground'}`}
              data-testid="unmatched-tab">
              <Unlink size={13} />Unmatched ({unmatchedData?.total || 0})
            </button>
            <button onClick={() => setMatchTab('alerts')}
              className={`px-4 py-1.5 text-sm rounded-lg transition-colors flex items-center gap-1.5 ${matchTab === 'alerts' ? 'bg-white dark:bg-stone-700 shadow font-medium' : 'text-muted-foreground hover:text-foreground'}`}
              data-testid="alerts-tab">
              <Bell size={13} />Alerts ({alertHistory.length})
            </button>
          </div>
        )}

        {/* Auto-Match Results */}
        {matchTab === 'auto-match' && reconciliation && (
          <Card className="dark:bg-foreground dark:border-stone-700">
            <CardContent className="p-0">
              {autoMatches.length === 0 ? (
                <div className="p-12 text-center">
                  <Wand2 size={40} className="mx-auto text-muted-foreground mb-3" />
                  <p className="text-muted-foreground font-medium">No Auto-Matches Yet</p>
                  <p className="text-xs text-muted-foreground mt-1">Click "Auto-Match" to match bank transactions to sales, expenses, and supplier payments</p>
                </div>
              ) : (
                <>
                  {/* Phase F2 — Match status & entity-type filters */}
                  <div className="px-3 py-2 border-b flex flex-wrap items-center gap-2 text-xs" data-testid="match-filters">
                    <span className="font-bold uppercase tracking-wider text-muted-foreground">Status:</span>
                    {[
                      { k: 'all', l: 'All' },
                      { k: 'confirmed', l: 'Confirmed' },
                      { k: 'auto_confirmed', l: 'Auto-confirmed' },
                      { k: 'auto', l: 'Pending review' },
                    ].map(opt => (
                      <button key={opt.k} onClick={() => setMatchStatusFilter(opt.k)}
                        data-testid={`match-status-${opt.k}`}
                        className={`px-2 py-0.5 rounded-full ${matchStatusFilter === opt.k ? 'bg-primary text-primary-foreground' : 'bg-muted hover:bg-muted/70'}`}>
                        {opt.l}
                      </button>
                    ))}
                    <span className="font-bold uppercase tracking-wider text-muted-foreground ml-3">Entity:</span>
                    {['all', 'customer', 'supplier', 'partner', 'employee'].map(et => (
                      <button key={et} onClick={() => setMatchEntityFilter(et)}
                        data-testid={`match-entity-${et}`}
                        className={`px-2 py-0.5 rounded-full capitalize ${matchEntityFilter === et ? 'bg-primary text-primary-foreground' : 'bg-muted hover:bg-muted/70'}`}>
                        {et}
                      </button>
                    ))}
                  </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="auto-match-table">
                    <thead>
                      <tr className="bg-muted/50 dark:bg-muted border-y text-xs">
                        <th className="text-left p-2.5 font-medium">Bank Transaction</th>
                        <th className="text-right p-2.5 font-medium">Amount</th>
                        <th className="text-center p-2.5 font-medium">Match</th>
                        <th className="text-left p-2.5 font-medium">System Record</th>
                        <th className="text-left p-2.5 font-medium">Entity</th>
                        <th className="text-right p-2.5 font-medium">Amount</th>
                        <th className="text-center p-2.5 font-medium">Confidence</th>
                        <th className="text-center p-2.5 font-medium">Status</th>
                        <th className="text-center p-2.5 font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {autoMatches
                        .filter(m => {
                          if (matchStatusFilter === 'all') return true;
                          if (matchStatusFilter === 'auto_confirmed') return !!m.auto_confirmed;
                          if (matchStatusFilter === 'confirmed') return m.status === 'confirmed';
                          if (matchStatusFilter === 'auto') return m.status === 'auto';
                          return true;
                        })
                        .filter(m => matchEntityFilter === 'all' || m.entity_hint?.entity_type === matchEntityFilter)
                        .map((m, idx) => (
                        <tr key={m.id} className={`border-b hover:bg-muted/50 ${m.status === 'confirmed' ? 'bg-success/10' : ''}`} data-testid={`match-row-${idx}`}>
                          <td className="p-2.5">
                            <p className="text-xs font-medium">{m.txn_date}</p>
                            <p className="text-[10px] text-muted-foreground truncate max-w-[200px]">{m.txn_desc}</p>
                          </td>
                          <td className="p-2.5 text-right font-mono text-xs">{cur()} {m.txn_amount?.toLocaleString()}</td>
                          <td className="p-2.5 text-center">
                            <Badge className={`text-[9px] ${m.match_type === 'sale' ? 'bg-success' : m.match_type === 'expense' ? 'bg-error' : 'bg-info'}`}>
                              {m.match_type === 'sale' ? 'Sale' : m.match_type === 'expense' ? 'Expense' : 'Supplier'}
                            </Badge>
                          </td>
                          <td className="p-2.5">
                            <p className="text-xs font-medium">{m.match_date}</p>
                            <p className="text-[10px] text-muted-foreground truncate max-w-[200px]">{m.match_desc}</p>
                          </td>
                          <td className="p-2.5">
                            {m.entity_hint ? (
                              <div className="flex flex-col gap-0.5" data-testid={`entity-hint-${idx}`}>
                                <span className={`text-[9px] uppercase font-bold tracking-wider ${
                                  m.entity_hint.entity_type === 'customer' ? 'text-info' :
                                  m.entity_hint.entity_type === 'supplier' ? 'text-warning' :
                                  m.entity_hint.entity_type === 'partner' ? 'text-violet-600' :
                                  m.entity_hint.entity_type === 'employee' ? 'text-emerald-600' : 'text-muted-foreground'
                                }`}>{m.entity_hint.entity_type}</span>
                                <span className="text-[10px] font-medium truncate max-w-[140px]">{m.entity_hint.name}</span>
                                {m.entity_hint.confidence === 'high' && <span className="text-[9px] text-success">✓ {m.entity_hint.match_field}</span>}
                                {m.entity_hint.confidence === 'medium' && <span className="text-[9px] text-warning">~ {m.entity_hint.match_field}</span>}
                              </div>
                            ) : <span className="text-[10px] text-muted-foreground">—</span>}
                          </td>
                          <td className="p-2.5 text-right font-mono text-xs">{cur()} {m.match_amount?.toLocaleString()}</td>
                          <td className="p-2.5 text-center">
                            <span className={`inline-block px-1.5 py-0.5 rounded text-[9px] font-bold ${m.confidence >= 90 ? 'bg-success/20 text-success dark:bg-emerald-900/30 dark:text-emerald-400' : m.confidence >= 65 ? 'bg-warning/20 text-warning dark:bg-amber-900/30 dark:text-amber-400' : 'bg-error/20 text-error dark:bg-red-900/30 dark:text-red-400'}`}>
                              {m.confidence >= 90 ? 'Exact' : m.confidence >= 65 ? 'Probable' : 'Possible'} {m.confidence}%
                            </span>
                          </td>
                          <td className="p-2.5 text-center">
                            <Badge variant="outline" className={`text-[9px] ${m.auto_confirmed ? 'border-violet-300 text-violet-700' : m.status === 'confirmed' ? 'border-success/30 text-success' : 'border-border'}`}>
                              {m.auto_confirmed ? 'Auto-confirmed' : m.status === 'confirmed' ? 'Confirmed' : 'Pending'}
                            </Badge>
                          </td>
                          <td className="p-2.5 text-center">
                            {m.status !== 'confirmed' ? (
                              <div className="flex gap-1 justify-center">
                                <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-success hover:text-success" onClick={() => confirmMatch(m.id)} data-testid={`confirm-match-${idx}`}>
                                  <CheckCircle size={14} />
                                </Button>
                                <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-400 hover:text-error" onClick={() => rejectMatch(m.id)} data-testid={`reject-match-${idx}`}>
                                  <Unlink size={14} />
                                </Button>
                              </div>
                            ) : (
                              <CheckCircle size={14} className="text-success mx-auto" />
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                </>
              )}
            </CardContent>
          </Card>
        )}

        {/* Unmatched Transactions */}
        {matchTab === 'unmatched' && reconciliation && (
          <Card className="dark:bg-foreground dark:border-stone-700">
            <CardContent className="p-0">
              {unmatchedLoading ? (
                <div className="p-12 text-center text-muted-foreground">Loading unmatched transactions...</div>
              ) : !unmatchedData || unmatchedData.unmatched.length === 0 ? (
                <div className="p-12 text-center">
                  <CheckCircle size={40} className="mx-auto text-emerald-300 mb-3" />
                  <p className="text-muted-foreground font-medium">All Transactions Matched</p>
                  <p className="text-xs text-muted-foreground mt-1">No unmatched bank transactions found. Try running Auto-Match first.</p>
                </div>
              ) : (
                <div className="divide-y dark:divide-stone-700" data-testid="unmatched-list">
                  {unmatchedData.unmatched.map((u, idx) => (
                    <div key={u.index} className="p-4 hover:bg-muted/50 dark:hover:bg-muted/50" data-testid={`unmatched-row-${idx}`}>
                      <div className="flex items-start justify-between gap-4 mb-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <span className="font-mono text-xs">{u.date}</span>
                            <Badge variant="outline" className={`text-[9px] ${u.type === 'credit' ? 'border-success/30 text-success' : 'border-error/30 text-error'}`}>
                              {u.type}
                            </Badge>
                            {u.category && <Badge variant="outline" className="text-[9px]">{u.category.replace(/_/g, ' ')}</Badge>}
                            {u.entity_hint && (
                              <span className={`text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full ${
                                u.entity_hint.confidence === 'high' ? 'bg-success/20 text-success' :
                                u.entity_hint.confidence === 'medium' ? 'bg-warning/20 text-warning' :
                                'bg-info/20 text-info'
                              }`} data-testid={`unmatched-entity-${idx}`}
                                title={`Suggested ${u.entity_hint.entity_type}: ${u.entity_hint.name} (${u.entity_hint.match_field}, ${u.entity_hint.confidence})`}>
                                ✨ {u.entity_hint.entity_type}: {u.entity_hint.name}
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground truncate">{u.description || 'No description'}</p>
                          {u.beneficiary && <p className="text-[10px] text-muted-foreground">Beneficiary: {u.beneficiary}</p>}
                        </div>
                        <span className={`text-lg font-bold font-mono ${u.type === 'credit' ? 'text-success' : 'text-error'}`}>
                          {cur()} {u.amount?.toLocaleString()}
                        </span>
                      </div>
                      {u.suggestions.length > 0 ? (
                        <div className="ml-4 border-l-2 border-border dark:border-stone-700 pl-3 space-y-2 mt-3">
                          <p className="text-[10px] font-semibold uppercase text-muted-foreground">Suggested Matches</p>
                          {u.suggestions.map((s, si) => (
                            <div key={si} className="flex items-center gap-3 bg-muted/50 dark:bg-muted rounded-lg p-2.5">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-0.5">
                                  <Badge className={`text-[8px] ${s.type === 'sale' ? 'bg-success' : s.type === 'expense' ? 'bg-error' : 'bg-info'}`}>
                                    {s.type === 'sale' ? 'Sale' : s.type === 'expense' ? 'Expense' : 'Supplier'}
                                  </Badge>
                                  <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${s.tier === 'exact' ? 'bg-success/20 text-success' : s.tier === 'probable' ? 'bg-warning/20 text-warning' : 'bg-error/20 text-error'}`}>
                                    {s.tier} {s.score}%
                                  </span>
                                  <span className="text-[10px] text-muted-foreground">{s.date}</span>
                                </div>
                                <p className="text-xs truncate">{s.desc}</p>
                                <p className="text-[10px] text-muted-foreground">Amount: {cur()} {s.amount?.toLocaleString()} (diff: {cur()} {s.amt_diff})</p>
                              </div>
                              <Button size="sm" variant="outline" className="h-7 text-xs shrink-0 border-success/30 text-success hover:bg-success/10"
                                onClick={() => manualLink(u.index, s.type === 'sale' ? 'sale' : s.type === 'expense' ? 'expense' : 'supplier_payment', s.id)}
                                data-testid={`link-btn-${idx}-${si}`}>
                                <Link size={12} className="mr-1" />Link
                              </Button>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-[10px] text-muted-foreground ml-4 mt-2">No matching suggestions found</p>
                      )}
                      {/* Top-3 entity candidates picker (iter330) — uses /bank-matching/suggest
                          to surface customers/suppliers/employees/platforms by IBAN/name. */}
                      <div className="ml-4 mt-2 flex items-center gap-2 flex-wrap" data-testid={`top3-row-${idx}`}>
                        <span className="text-[10px] font-semibold uppercase text-muted-foreground">Entity candidates:</span>
                        <MatchCandidatesPicker
                          row={{
                            description: u.description,
                            amount: u.amount,
                            party_name: (u.entity_hint && u.entity_hint.name) || u.beneficiary || '',
                          }}
                          testidPrefix={`top3-${idx}`}
                          onPick={(c) => {
                            const t = c.entity_type === 'customer' ? 'customer'
                              : c.entity_type === 'supplier' ? 'supplier_payment'
                              : c.entity_type === 'employee' ? 'employee_payment'
                              : c.entity_type === 'platform' ? 'platform_payment'
                              : 'sale';
                            manualLink(u.index, t, c.id || c.entity_id);
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Alerts Tab */}
        {matchTab === 'alerts' && (
          <div className="space-y-4" data-testid="alerts-section">
            {/* Alert Settings Card */}
            <Card className="dark:bg-foreground dark:border-stone-700">
              <CardHeader className="py-3 px-4">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm font-outfit flex items-center gap-2">
                    <Settings size={14} /> Alert Settings
                  </CardTitle>
                  <div className="flex items-center gap-2">
                    <Button size="sm" variant="outline" onClick={runAlertNow} disabled={runningAlert} data-testid="run-alert-btn"
                      className="border-orange-200 text-orange-600 hover:bg-orange-50">
                      <Play size={13} className="mr-1" />{runningAlert ? 'Running...' : 'Run Now'}
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pb-4">
                {alertSettings && (
                  <div className="flex flex-wrap items-center gap-4">
                    <div className="flex items-center gap-2">
                      <Label className="text-xs text-muted-foreground whitespace-nowrap">Threshold (SR):</Label>
                      <Select value={String(alertSettings.threshold)} onValueChange={v => saveAlertSettings({ threshold: parseInt(v) })} data-testid="alert-threshold">
                        <SelectTrigger className="h-8 w-24 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="100">100</SelectItem>
                          <SelectItem value="250">250</SelectItem>
                          <SelectItem value="500">500</SelectItem>
                          <SelectItem value="1000">1,000</SelectItem>
                          <SelectItem value="2500">2,500</SelectItem>
                          <SelectItem value="5000">5,000</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex items-center gap-2">
                      <Label className="text-xs text-muted-foreground whitespace-nowrap">Schedule:</Label>
                      <Select value={alertSettings.day_of_week} onValueChange={v => saveAlertSettings({ day_of_week: v })} data-testid="alert-day">
                        <SelectTrigger className="h-8 w-24 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="sun">Sunday</SelectItem>
                          <SelectItem value="mon">Monday</SelectItem>
                          <SelectItem value="tue">Tuesday</SelectItem>
                          <SelectItem value="wed">Wednesday</SelectItem>
                          <SelectItem value="thu">Thursday</SelectItem>
                          <SelectItem value="fri">Friday</SelectItem>
                          <SelectItem value="sat">Saturday</SelectItem>
                        </SelectContent>
                      </Select>
                      <span className="text-xs text-muted-foreground">at</span>
                      <Select value={String(alertSettings.hour)} onValueChange={v => saveAlertSettings({ hour: parseInt(v) })} data-testid="alert-hour">
                        <SelectTrigger className="h-8 w-20 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {[6,7,8,9,10,11,12,13,14,15,16,17,18,19,20,21].map(h => (
                            <SelectItem key={h} value={String(h)}>{h}:00</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex items-center gap-2">
                      <Label className="text-xs text-muted-foreground">Enabled:</Label>
                      <button
                        onClick={() => saveAlertSettings({ enabled: !alertSettings.enabled })}
                        data-testid="alert-toggle"
                        className={`relative w-10 h-5 rounded-full transition-colors ${alertSettings.enabled ? 'bg-success' : 'bg-stone-300 dark:bg-stone-600'}`}>
                        <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform ${alertSettings.enabled ? 'translate-x-5' : ''}`} />
                      </button>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Label className="text-xs text-muted-foreground">Channels:</Label>
                      {['whatsapp', 'push', 'email'].map(ch => (
                        <Badge key={ch} variant="outline"
                          className={`text-[9px] cursor-pointer transition-colors ${(alertSettings.channels || []).includes(ch) ? 'bg-success/20 text-success border-success/30 dark:bg-emerald-900/30 dark:text-emerald-400' : 'opacity-40'}`}
                          onClick={() => {
                            const chs = alertSettings.channels || [];
                            const updated = chs.includes(ch) ? chs.filter(c => c !== ch) : [...chs, ch];
                            saveAlertSettings({ channels: updated });
                          }}>
                          {ch}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Alert History */}
            <Card className="dark:bg-foreground dark:border-stone-700">
              <CardHeader className="py-3 px-4">
                <CardTitle className="text-sm font-outfit flex items-center gap-2">
                  <Clock size={14} /> Alert History
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {alertLoading ? (
                  <div className="p-8 text-center text-muted-foreground">Loading alerts...</div>
                ) : alertHistory.length === 0 ? (
                  <div className="p-8 text-center">
                    <Bell size={36} className="mx-auto text-muted-foreground mb-3" />
                    <p className="text-muted-foreground text-sm font-medium">No Alerts Yet</p>
                    <p className="text-xs text-muted-foreground mt-1">Click "Run Now" to generate your first reconciliation alert.</p>
                  </div>
                ) : (
                  <div className="divide-y dark:divide-stone-700" data-testid="alert-history-list">
                    {alertHistory.map((alert, idx) => (
                      <div key={alert.id} className="p-4 hover:bg-muted/50 dark:hover:bg-muted/50" data-testid={`alert-row-${idx}`}>
                        <div className="flex items-start justify-between gap-3 mb-2">
                          <div className="flex items-center gap-2">
                            {alert.status === 'flagged' ? (
                              <AlertOctagon size={18} className="text-error shrink-0" />
                            ) : (
                              <Shield size={18} className="text-success shrink-0" />
                            )}
                            <div>
                              <p className="text-sm font-medium">
                                {alert.status === 'flagged'
                                  ? `${alert.total_flagged} flagged transactions above ${cur()} ${alert.threshold?.toLocaleString()}`
                                  : 'All clear — no flagged transactions'}
                              </p>
                              <p className="text-[10px] text-muted-foreground">
                                {new Date(alert.created_at).toLocaleString()} &middot; {alert.total_statements} statements &middot; {alert.total_unmatched} unmatched total
                              </p>
                            </div>
                          </div>
                          <Badge className={alert.status === 'flagged' ? 'bg-error/20 text-error dark:bg-red-900/30 dark:text-red-400' : 'bg-success/20 text-success dark:bg-emerald-900/30 dark:text-emerald-400'}>
                            {alert.status === 'flagged' ? 'Flagged' : 'Clean'}
                          </Badge>
                        </div>

                        {/* Statement summaries */}
                        {alert.statement_summaries?.length > 0 && (
                          <div className="ml-7 space-y-1.5 mb-2">
                            {alert.statement_summaries.map((ss, si) => (
                              <div key={si} className="flex items-center gap-2 text-xs">
                                <span className="font-medium">{ss.file_name}</span>
                                <span className="text-muted-foreground">{ss.match_rate}% matched</span>
                                <span className="text-muted-foreground">({ss.matched}/{ss.total_txns})</span>
                                {ss.flagged > 0 && (
                                  <Badge variant="outline" className="text-[8px] border-error/30 text-error">{ss.flagged} flagged (SR {ss.flagged_amount?.toLocaleString()})</Badge>
                                )}
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Flagged items preview */}
                        {alert.flagged_items?.length > 0 && (
                          <div className="ml-7 border-l-2 border-error/30 dark:border-red-800 pl-3 space-y-1">
                            <p className="text-[10px] font-semibold uppercase text-muted-foreground">Top Flagged</p>
                            {alert.flagged_items.slice(0, 4).map((fi, fii) => (
                              <div key={fii} className="flex items-center gap-2 text-xs">
                                <span className="font-mono">{fi.date}</span>
                                <Badge variant="outline" className={`text-[8px] ${fi.type === 'credit' ? 'border-success/30 text-success' : 'border-error/30 text-error'}`}>{fi.type}</Badge>
                                <span className="font-semibold">{cur()} {fi.amount?.toLocaleString()}</span>
                                <span className="text-muted-foreground truncate max-w-[200px]">{fi.description}</span>
                              </div>
                            ))}
                            {alert.flagged_items.length > 4 && (
                              <p className="text-[10px] text-muted-foreground">+ {alert.flagged_items.length - 4} more</p>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Reconciliation Table */}
        {loading && <div className="text-center py-8 text-muted-foreground">Loading reconciliation data...</div>}
        {matchTab === 'reconciliation' && !loading && reconciliation && (
          <Card className="dark:bg-foreground dark:border-stone-700">
            <CardHeader className="py-3 px-4">
              <CardTitle className="text-sm font-outfit dark:text-white">
                {filteredRows.length} of {rows.length} rows
                {statusFilter !== 'all' && <Badge variant="outline" className="ml-2 text-xs">{statusFilter}</Badge>}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="recon-table">
                  <thead>
                    <tr className="bg-muted/50 dark:bg-muted border-y border-border dark:border-stone-700">
                      <th className="text-left p-2.5 font-medium text-xs dark:text-muted-foreground">Deposit Date</th>
                      <th className="text-left p-2.5 font-medium text-xs dark:text-muted-foreground">Sale Date</th>
                      <th className="text-left p-2.5 font-medium text-xs dark:text-muted-foreground">Branch</th>
                      <th className="text-right p-2.5 font-medium text-xs dark:text-muted-foreground">Bank POS (SR)</th>
                      <th className="text-right p-2.5 font-medium text-xs dark:text-muted-foreground">App Sales (SR)</th>
                      <th className="text-right p-2.5 font-medium text-xs dark:text-muted-foreground">Difference</th>
                      <th className="text-right p-2.5 font-medium text-xs dark:text-muted-foreground">Diff %</th>
                      <th className="text-center p-2.5 font-medium text-xs dark:text-muted-foreground">Status</th>
                      <th className="text-center p-2.5 font-medium text-xs dark:text-muted-foreground">Flag</th>
                      <th className="text-center p-2.5 font-medium text-xs dark:text-muted-foreground">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRows.map((row, idx) => {
                      const cfg = STATUS_CONFIG[row.status] || STATUS_CONFIG.mismatch;
                      const Icon = cfg.icon;
                      const rowKey = `${row.deposit_date}|${row.branch}`;
                      const rowFlag = flags[rowKey];
                      const diffPct = row.bank_amount > 0 ? ((Math.abs(row.difference) / row.bank_amount) * 100).toFixed(1) : '0.0';
                      return (
                        <tr key={idx} className={`border-b border-stone-50 dark:border-stone-800 hover:bg-muted/50 dark:hover:bg-muted/50 transition-colors ${row.status === 'mismatch' ? 'bg-warning/10 dark:bg-amber-900/10' : row.status === 'bank_only' || row.status === 'app_only' ? 'bg-error/10 dark:bg-red-900/10' : ''}`}
                          data-testid={`recon-row-${idx}`}>
                          <td className="p-2.5 font-mono text-xs dark:text-muted-foreground">{row.deposit_date}</td>
                          <td className="p-2.5 font-mono text-xs dark:text-muted-foreground">{row.sale_date || '-'}</td>
                          <td className="p-2.5 font-medium dark:text-stone-200">{row.branch}</td>
                          <td className="p-2.5 text-right font-mono dark:text-muted-foreground">{row.bank_amount?.toLocaleString()}</td>
                          <td className="p-2.5 text-right font-mono dark:text-muted-foreground">{row.app_amount?.toLocaleString()}</td>
                          <td className={`p-2.5 text-right font-mono font-medium ${row.difference > 0 ? 'text-warning' : row.difference < 0 ? 'text-error' : 'text-success'}`}>
                            {row.difference > 0 ? '+' : ''}{row.difference?.toLocaleString()}
                          </td>
                          <td className={`p-2.5 text-right font-mono text-xs ${parseFloat(diffPct) > 5 ? 'text-error font-bold' : parseFloat(diffPct) > 1 ? 'text-warning' : 'text-muted-foreground'}`}>
                            {row.status === 'matched' ? '0%' : `${diffPct}%`}
                          </td>
                          <td className="p-2.5 text-center">
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${cfg.color}`}>
                              <Icon size={11} />{cfg.label}
                            </span>
                          </td>
                          <td className="p-2.5 text-center">
                            {rowFlag?.flag ? (
                              <Badge variant="outline" className={`text-[10px] ${rowFlag.flag === 'verified' ? 'border-success/30 text-success dark:border-emerald-700 dark:text-emerald-400' : rowFlag.flag === 'investigate' ? 'border-warning/30 text-warning' : rowFlag.flag === 'resolved' ? 'border-info/30 text-info' : 'border-border text-muted-foreground'}`}>
                                {FLAG_OPTIONS.find(f => f.value === rowFlag.flag)?.label || rowFlag.flag}
                              </Badge>
                            ) : <span className="text-muted-foreground dark:text-muted-foreground">-</span>}
                          </td>
                          <td className="p-2.5 text-center">
                            <Button size="sm" variant="ghost" className="h-7 w-7 p-0" data-testid={`flag-btn-${idx}`}
                              onClick={() => {
                                setFlagDialog({ row_key: rowKey, row });
                                setFlagForm({ flag: rowFlag?.flag || '', notes: rowFlag?.notes || '' });
                              }}>
                              <Flag size={13} className={rowFlag?.flag ? 'text-orange-500' : 'text-muted-foreground'} />
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                    {filteredRows.length === 0 && (
                      <tr><td colSpan={10} className="p-8 text-center text-muted-foreground">
                        {rows.length === 0 ? 'Select a bank statement to view reconciliation' : 'No rows match the current filter'}
                      </td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {!selectedStmt && !loading && (
          <Card className="dark:bg-foreground dark:border-stone-700 border-dashed border-2">
            <CardContent className="py-16 text-center">
              <ArrowDownUp size={40} className="mx-auto text-muted-foreground dark:text-muted-foreground mb-3" />
              <p className="text-muted-foreground font-medium">Select a Bank Statement</p>
              <p className="text-xs text-muted-foreground mt-1">Choose a statement above to compare bank deposits against Smarto sales</p>
            </CardContent>
          </Card>
        )}

        {/* Flag Dialog */}
        <Dialog open={!!flagDialog} onOpenChange={(v) => !v && setFlagDialog(null)}>
          <DialogContent className="max-w-sm">
            <DialogHeader><DialogTitle className="font-outfit text-base">Flag Row</DialogTitle></DialogHeader>
            {flagDialog && (
              <div className="space-y-3">
                <div className="text-xs text-muted-foreground bg-muted/50 dark:bg-muted p-2 rounded-lg">
                  <span className="font-medium dark:text-stone-200">{flagDialog.row.branch}</span> on {flagDialog.row.deposit_date}
                  <br />Bank: {cur()} {flagDialog.row.bank_amount?.toLocaleString()} | App: {cur()} {flagDialog.row.app_amount?.toLocaleString()}
                </div>
                <div>
                  <Label className="text-xs">Flag Status</Label>
                  <Select value={flagForm.flag} onValueChange={(v) => setFlagForm({ ...flagForm, flag: v })}>
                    <SelectTrigger className="h-9" data-testid="flag-select"><SelectValue placeholder="Choose flag..." /></SelectTrigger>
                    <SelectContent>
                      {FLAG_OPTIONS.map(f => <SelectItem key={f.value || '__none'} value={f.value || '__none'}>{f.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Notes</Label>
                  <Textarea value={flagForm.notes} onChange={(e) => setFlagForm({ ...flagForm, notes: e.target.value })}
                    placeholder="Add notes about this row..." className="h-20 text-sm" data-testid="flag-notes" />
                </div>
                <div className="flex gap-2">
                  <Button size="sm" className="flex-1" data-testid="save-flag-btn" onClick={saveFlag}>Save Flag</Button>
                  <Button size="sm" variant="outline" onClick={() => setFlagDialog(null)}>Cancel</Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
